import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { RundownWithSections, UpdateRundownRequest, AddSectionRequest, AddItemRequest, UpdateItemRequest, UpdateClaimsRequest } from '@news-assist/shared';

export function useRundown(id: string) {
  return useQuery({
    queryKey: ['rundown', id],
    queryFn: () => apiClient.get<RundownWithSections>(`/rundowns/${id}`),
    enabled: !!id,
  });
}

export function useCreateRundown() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { title: string; targetDate?: string; notes?: string }) =>
      apiClient.post<{ id: string; title: string }>('/rundowns', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rundowns'] });
    },
  });
}

export function useUpdateRundown(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateRundownRequest) =>
      apiClient.patch<{ success: boolean }>(`/rundowns/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rundown', id] });
      queryClient.invalidateQueries({ queryKey: ['rundowns'] });
    },
  });
}

export function useDeleteRundown() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ success: boolean }>(`/rundowns/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rundowns'] });
    },
  });
}

// Section mutations
export function useAddSection(rundownId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddSectionRequest) =>
      apiClient.post<{ id: string; position: number }>(`/rundowns/${rundownId}/sections`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rundown', rundownId] });
    },
  });
}

export function useDeleteSection(rundownId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionId: string) =>
      apiClient.delete<{ success: boolean }>(`/rundowns/${rundownId}/sections/${sectionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rundown', rundownId] });
    },
  });
}

export function useReorderSections(rundownId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionIds: string[]) =>
      apiClient.put<{ success: boolean }>(`/rundowns/${rundownId}/sections/reorder`, { sectionIds }),
    onMutate: async (sectionIds) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['rundown', rundownId] });

      // Snapshot previous value
      const previousRundown = queryClient.getQueryData<RundownWithSections>(['rundown', rundownId]);

      // Optimistically update
      if (previousRundown) {
        const reorderedSections = sectionIds
          .map(id => previousRundown.sections.find(s => s.id === id))
          .filter(Boolean) as typeof previousRundown.sections;

        queryClient.setQueryData<RundownWithSections>(['rundown', rundownId], {
          ...previousRundown,
          sections: reorderedSections,
        });
      }

      return { previousRundown };
    },
    onError: (_err, _sectionIds, context) => {
      // Rollback on error
      if (context?.previousRundown) {
        queryClient.setQueryData(['rundown', rundownId], context.previousRundown);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['rundown', rundownId] });
    },
  });
}

// Item mutations
export function useAddRundownItem(rundownId: string, sectionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { storyId: string; size?: string; style?: string }) =>
      apiClient.post<{ id: string; claimsAdded: number }>(`/rundowns/${rundownId}/sections/${sectionId}/items`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rundown', rundownId] });
    },
  });
}

export function useUpdateRundownItem(rundownId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, ...data }: UpdateItemRequest & { itemId: string }) =>
      apiClient.patch<{ success: boolean }>(`/rundowns/${rundownId}/items/${itemId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rundown', rundownId] });
    },
  });
}

export function useDeleteRundownItem(rundownId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) =>
      apiClient.delete<{ success: boolean }>(`/rundowns/${rundownId}/items/${itemId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rundown', rundownId] });
    },
  });
}

export function useReorderRundownItems(rundownId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sectionId, itemIds }: { sectionId: string; itemIds: string[] }) =>
      apiClient.put<{ success: boolean }>(`/rundowns/${rundownId}/sections/${sectionId}/items/reorder`, { itemIds }),
    onMutate: async ({ sectionId, itemIds }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['rundown', rundownId] });

      // Snapshot previous value
      const previousRundown = queryClient.getQueryData<RundownWithSections>(['rundown', rundownId]);

      // Optimistically update
      if (previousRundown) {
        const updatedSections = previousRundown.sections.map(section => {
          if (section.id !== sectionId) return section;

          const reorderedItems = itemIds
            .map(id => section.items.find(i => i.id === id))
            .filter(Boolean) as typeof section.items;

          return { ...section, items: reorderedItems };
        });

        queryClient.setQueryData<RundownWithSections>(['rundown', rundownId], {
          ...previousRundown,
          sections: updatedSections,
        });
      }

      return { previousRundown };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previousRundown) {
        queryClient.setQueryData(['rundown', rundownId], context.previousRundown);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['rundown', rundownId] });
    },
  });
}

export function useUpdateRundownClaims(rundownId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, claims }: { itemId: string; claims: UpdateClaimsRequest['claims'] }) =>
      apiClient.patch<{ success: boolean }>(`/rundowns/${rundownId}/items/${itemId}/claims`, { claims }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rundown', rundownId] });
    },
  });
}
